import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import * as Yup from 'yup';
import i18n from 'i18n-js';
import Grid from '@material-ui/core/Grid';
import { BaseModal } from 'components/UI';
import { Formik, Form, Field } from 'formik';
import { AuthActions } from 'state/modules/auth';
import { TextField } from 'components/FormFields';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Typography } from '@material-ui/core';
import { FETCH_STATE } from 'constants/common';
import useStyles from './ChangePassword.styled';

const ChangeAccountPasswordModal = ({ user, open, handleClick }) => {
  const classes = useStyles();

  const formSchema = Yup.object().shape({
    password: Yup.string().required(i18n.t('common.required_field')),
    new_password: Yup.string()
      .required(i18n.t('common.required_field'))
      .matches(
        '^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#-._$%^&*])(?=.{8,})',
        i18n.t('common.password_match')
      ),
  });

  const formRef = useRef(null);
  const dispatch = useDispatch();
  const {
    fetchingUpdateAccount,
    trustToken,
    twoFactorId,
    startingTwoFactor,
    fetchingTwoFactorLogin,
  } = useSelector((state) => state.auth);

  const onSubmit = () => {
    formRef.current.submitForm();
  };

  useEffect(() => {
    if (user.ciam_id) {
      dispatch(AuthActions.fetchStartTwoFactor());
    }
  }, [user]);

  return (
    <BaseModal
      size="sm"
      open={open}
      hasEnterButton
      hasCancelButton
      onEnter={onSubmit}
      onCancel={handleClick}
      enableCloseButton={false}
      handleClick={handleClick}
      loading={fetchingUpdateAccount}
      title={i18n.t('account.change_password')}
      enterButtonLabel={i18n.t('account.change_password')}
      disabled={(twoFactorId && !trustToken) || startingTwoFactor === FETCH_STATE.LOADING}
    >
      <Formik
        innerRef={formRef}
        initialValues={{
          code: '',
          password: '',
          new_password: '',
        }}
        onSubmit={(values) => {
          if (user.ciam_id) {
            dispatch(
              AuthActions.fetchChangePasswordFA(
                {
                  trustToken,
                  currentPassword: formRef?.current?.values?.password,
                  password: formRef?.current?.values?.new_password,
                },
                handleClick
              )
            );
          } else {
            dispatch(
              AuthActions.fetchChangePassword(
                { password: values.password, new_password: values.new_password },
                handleClick
              )
            );
          }
        }}
        validationSchema={formSchema}
      >
        {({ handleSubmit }) => (
          <Form noValidate onSubmit={handleSubmit} autoComplete="off">
            <Grid data-cy="change-password-modal" container spacing={2}>
              {user.ciam_id && (twoFactorId || startingTwoFactor === FETCH_STATE.LOADING) && (
                <>
                  <Grid data-cy="mfa-required" item xs={12}>
                    <Typography className={classes.label}>
                      {i18n.t('account.mfa_required')}
                    </Typography>
                    <Typography className={classes.value}>
                      {i18n.t('account.mfa_description')}
                    </Typography>
                    <Grid item xs={12}>
                      <Field
                        data-cy="code-input"
                        margin="none"
                        type="string"
                        name="code"
                        component={TextField}
                        label={i18n.t('account.mfa_code')}
                        asyncErrors={
                          fetchingTwoFactorLogin === FETCH_STATE.ERROR
                            ? i18n.t('account.mfa_error')
                            : null
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                    data-cy="verify-btn"
                      fullWidth
                      type="button"
                      variant="contained"
                      color="primary"
                      className={classes.button}
                      onClick={() => {
                        dispatch(
                          AuthActions.fetchTwoFactorLogin({
                            code: formRef?.current?.values?.code,
                            twoFactorId: twoFactorId,
                          })
                        );
                      }}
                      disabled={
                        formRef?.current?.values?.code?.length !== 6 ||
                        !twoFactorId ||
                        fetchingTwoFactorLogin === FETCH_STATE.LOADING
                      }
                    >
                      {i18n.t('account.mfa_verify')}
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                    data-cy="resend-btn"
                      fullWidth
                      type="button"
                      variant="outlined"
                      color="primary"
                      className={classes.button}
                      onClick={() => {
                        dispatch(AuthActions.fetchStartTwoFactor());
                      }}
                      disabled={startingTwoFactor === FETCH_STATE.LOADING}
                    >
                      {i18n.t('account.mfa_generate_code')}
                    </Button>
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <Field
                data-cy="password-input"
                  margin="none"
                  type="password"
                  name="password"
                  component={TextField}
                  label={i18n.t('account.current_password')}
                  disabled={
                    (twoFactorId && !trustToken) || startingTwoFactor === FETCH_STATE.LOADING
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                data-cy="new-password-input"
                  type="password"
                  name="new_password"
                  component={TextField}
                  label={i18n.t('account.new_password')}
                  disabled={
                    (twoFactorId && !trustToken) || startingTwoFactor === FETCH_STATE.LOADING
                  }
                />
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </BaseModal>
  );
};

ChangeAccountPasswordModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClick: PropTypes.func.isRequired,
};

ChangeAccountPasswordModal.defaultProps = {
  open: false,
  handleClick: () => {},
};

export default ChangeAccountPasswordModal;
